import { normalizeLanguageCode } from "@/config/languages.js";

const COPY = {
  "en-GB": {
    askAnything: "Ask Fola anything",
    patientWelcome:
      "Welcome. I'm Fola, your AfriMed™ AI Clinical Assistant. I can help you understand where things are, prepare for appointments, find prescriptions, and get support while using AfriMed™. I do not replace your clinician, but I can guide you through the app and help you get ready for care.",
    patientStarterTitle: "How can I help today?",
    patientStarterIntro:
      "Choose a guided start or type your message below.",
    doctorWelcome:
      "I can help with thread summaries, draft replies, message-based SOAP drafts, and admin routing while staying inside AfriMed™ Messages.",
    doctorStarterTitle: "Choose a Fola task",
    doctorStarterIntro:
      "Choose the workflow you want. SOAP drafts here use the live message thread only.",
    actions: {
      checkSymptoms: "Check symptoms",
      appointmentsHelp: "Appointments help",
      askHealthQuestion: "Ask a health question",
      urgentHelp: "Urgent help",
      summarizeThread: "Summarize thread",
      draftSoap: "Draft SOAP from thread",
      draftReply: "Draft reply",
      adminRouting: "Routing help",
      followUpBooking: "Follow-up booking",
      escalationReview: "Escalation review",
      explainMedicines: "Explain my medicines",
      prepareQuestions: "Help me prepare questions",
      summarizeVisitNotes: "Summarise my visit notes",
      selectHospital: "Select hospital",
      selectDoctor: "Select doctor",
      yes: "Yes",
      no: "No",
    },
    states: {
      thinking: "Thinking...",
      error: "I could not respond right now. Please try again.",
      imageReceived: "I received your image.",
      imageReviewLimited:
        "I can help you prepare next steps, but I cannot review images directly in chat yet.",
      doctorNeedsThread:
        "Open Fola from a patient thread so I can build this from the live conversation.",
    },
    symptom: {
      intro:
        "Let us go step by step so I can keep the guidance safe and useful.",
      categoryTitle: "What is the main concern?",
      categoryIntro: "Pick the closest option to start the intake.",
      categories: {
        rash: "Rash or skin change",
        fever: "Fever",
        pain: "Pain",
        breathing: "Cough or breathing",
        stomach: "Stomach upset",
        other: "Other symptoms",
      },
      durationTitle: "How long has this been going on?",
      durationIntro:
        "A rough timeframe helps me decide the safest next question.",
      durations: {
        today: "Started today",
        threeDays: "1 to 3 days",
        moreThanThreeDays: "More than 3 days",
      },
      redFlagTitle: "Any urgent warning signs right now?",
      redFlagIntro:
        "Pick Yes if the symptom feels severe, fast-changing, or affects breathing, swelling, fainting, or confusion.",
      writeup:
        "Please type where it is happening, when it started, and whether it feels itchy, painful, hot, swollen, or is spreading.",
      redFlagNoticeTitle: "This needs urgent human review",
      redFlagNoticeBody:
        "Please seek urgent in-person care now or contact emergency services if the symptom is severe, spreading quickly, or affecting breathing.",
      lowRiskFollowUp:
        "Thank you. Share the details in your own words and I will help you organise the next safe step.",
    },
    appointments: {
      intro:
        "I can point you to the right booking flow without leaving Messages feeling disconnected.",
      title: "Where do you want to start?",
      subtitle:
        "Choose the path that fits best and I will route you into the right AfriMed™ flow.",
    },
    healthQuestion:
      "Tell me your question in your own words. I will keep the reply practical and safe. Fola can help in selected AfriMed™-supported languages.",
    urgentHelp:
      "If this feels like an emergency, please contact local emergency services or go to urgent in-person care immediately.",
    patientSupport: {
      medicinesDisabled:
        "Medicine explanations are currently turned off in Accessibility & Fola. You can turn them back on in Settings whenever you want.",
      medicinesPrompt:
        "Share the medicine name, dose, or prescription text and I will explain it in everyday language.",
      questionsDisabled:
        "Question preparation is currently turned off in Accessibility & Fola. Turn it on in Settings if you want Fola to help with this.",
      questionsPrompt:
        "Tell me the appointment goal or concern, and I will help you prepare useful questions before you see your doctor.",
      visitNotesDisabled:
        "Visit-note summaries are currently turned off in Accessibility & Fola. You can enable them again in Settings.",
      visitNotesPrompt:
        "Paste the visit notes, report, or consultation update here and I will help summarise the key points.",
      noRecordContext:
        "I do not have a saved myEHR visit note in this chat yet. Open My Health Record to review visit cards, prescriptions, tests, and notes.",
      recordContextPrefix:
        "This myEHR note says your clinician recorded",
      recordContextSafety:
        "If symptoms worsen, or if anything is unclear, please contact your clinician.",
    },
    doctorLane:
      "Tell me what you need in one message and I will help from inside this thread. SOAP drafts here use the live message thread only.",
    doctorSoapSupportText:
      "SOAP drafts here use only the current message thread. Doctor review is required before use.",
    doctorAdmin: {
      title: "What do you need help routing?",
      intro: "Choose the next operational step and I will keep it inside Messages.",
      supportText:
        "Fola can help prepare follow-up, escalation, and patient-facing messaging without breaking thread context.",
      followUpTitle: "Follow-up booking check",
      followUpBody:
        "Confirm whether the patient needs a follow-up booking, note the expected timing, and prepare a short reply with the next safe step.",
      escalationTitle: "Escalation review",
      escalationBody:
        "Use this when urgency is rising, red flags are unclear, or the patient may need in-person review. Summarize the risk clearly before replying.",
    },
    doctorCards: {
      sourceLabel: "Source",
      keyPointsTitle: "Key points",
      openItemsTitle: "Open items",
      noStrongSummary:
        "There is not enough patient detail in this thread yet to produce a stronger summary.",
      patientRaisedPrefix: "Patient raised",
      latestUpdatePrefix: "Latest update",
      clinicianResponsePrefix: "Clinician response so far",
      recentContextPrefix: "Recent patient context centres on",
      noPatientMessage: "No patient message captured yet.",
      noLaterPatientUpdate: "No later patient update beyond the opening message.",
      noClinicianReply: "No clinician reply captured yet.",
      confirmTimeline: "Confirm timeline, severity, and any red-flag symptoms.",
      collectHistory: "Collect patient history before finalising next steps.",
      followUpBooking: "Check whether a follow-up booking or escalation is needed.",
      sections: {
        subjective: "Subjective",
        objective: "Objective",
        assessment: "Assessment",
        plan: "Plan",
      },
      limitedSymptoms: "Patient-reported symptoms are still limited in this thread.",
      noObjectiveFindings:
        "No objective findings, vitals, or exam results were documented in the message thread.",
      provisionalAssessment:
        "Provisional messaging-only assessment. Needs clinician review against full history and any red flags.",
      planPrefix: "Continue with clinician follow-up and clarify",
      planFallback:
        "Clarify severity, duration, and next safe step. Consider booking or escalation if risk increases.",
      confidenceNote:
        "Drafted from the current message thread only. Doctor review is required before saving or sharing clinically.",
      replySupportText: "Edit for tone and clinical precision before sending.",
      useAsDraft: "Use as draft",
      replyPrefix:
        "Thank you for the update. I have noted that",
      replySuffix:
        "Please keep monitoring your symptoms and let me know if anything worsens, especially any breathing difficulty, severe pain, or rapid change.",
      replyFallback:
        "Thank you for the update. I have reviewed the thread and will guide the next safe step once a little more detail is available.",
    },
    smartRouting: {
      urgentBridge:
        "This sounds like it may need urgent human review.",
      adminBridge:
        "I can route appointment help from here without pushing you out of Messages too early.",
      doctorBridge:
        "I recognised a doctor workflow request. Here are the native Fola actions for that lane.",
    },
  },
  pt: {
    askAnything: "Pergunte qualquer coisa a Fola",
    patientWelcome:
      "Posso ajudar a verificar sintomas, tratar de agendamentos e preparar proximos passos seguros. O suporte linguistico esta a expandir.",
    patientStarterTitle: "Como posso ajudar hoje?",
    patientStarterIntro:
      "Escolha um inicio guiado ou escreva a sua mensagem abaixo.",
    doctorWelcome:
      "Posso ajudar com resumos de conversas, rascunhos de resposta, rascunhos SOAP baseados na conversa e encaminhamento administrativo dentro das Mensagens AfriMed.",
    doctorStarterTitle: "Escolha uma tarefa Fola",
    doctorStarterIntro:
      "Escolha o fluxo que pretende. Os rascunhos SOAP aqui usam apenas a conversa desta thread.",
    actions: {
      checkSymptoms: "Verificar sintomas",
      appointmentsHelp: "Ajuda com consultas",
      askHealthQuestion: "Fazer pergunta de saude",
      urgentHelp: "Ajuda urgente",
      summarizeThread: "Resumir conversa",
      draftSoap: "Rascunho SOAP da conversa",
      draftReply: "Rascunhar resposta",
      adminRouting: "Ajuda de encaminhamento",
      followUpBooking: "Marcacao de seguimento",
      escalationReview: "Rever escalacao",
      explainMedicines: "Explicar os meus medicamentos",
      prepareQuestions: "Ajudar-me a preparar perguntas",
      summarizeVisitNotes: "Resumir as minhas notas da consulta",
      selectHospital: "Selecionar hospital",
      selectDoctor: "Selecionar medico",
      yes: "Sim",
      no: "Nao",
    },
    states: {
      thinking: "A pensar...",
      error: "Nao consegui responder agora. Tente novamente.",
      imageReceived: "Recebi a sua imagem.",
      imageReviewLimited:
        "Posso ajudar a preparar os proximos passos, mas ainda nao consigo rever imagens diretamente no chat.",
      doctorNeedsThread:
        "Abra a Fola a partir de uma conversa de paciente para eu construir isto com a conversa real.",
    },
    symptom: {
      intro:
        "Vamos passo a passo para manter a orientacao segura e util.",
      categoryTitle: "Qual e a principal preocupacao?",
      categoryIntro: "Escolha a opcao mais proxima para iniciar.",
      categories: {
        rash: "Erupcao ou alteracao da pele",
        fever: "Febre",
        pain: "Dor",
        breathing: "Tosse ou respiracao",
        stomach: "Mal estar no estomago",
        other: "Outros sintomas",
      },
      durationTitle: "Ha quanto tempo isso acontece?",
      durationIntro:
        "Uma ideia do tempo ajuda-me a escolher a proxima pergunta mais segura.",
      durations: {
        today: "Comecou hoje",
        threeDays: "1 a 3 dias",
        moreThanThreeDays: "Mais de 3 dias",
      },
      redFlagTitle: "Existe algum sinal de alerta urgente agora?",
      redFlagIntro:
        "Escolha Sim se o sintoma for grave, estiver a piorar rapidamente ou afetar respiracao, inchaco, desmaio ou confusao.",
      writeup:
        "Escreva onde acontece, quando comecou e se coce, doi, esta quente, inchado ou a espalhar-se.",
      redFlagNoticeTitle: "Isto precisa de avaliacao humana urgente",
      redFlagNoticeBody:
        "Procure cuidados presenciais urgentes agora ou contacte os servicos de emergencia se o sintoma for grave, se espalhar rapidamente ou afetar a respiracao.",
      lowRiskFollowUp:
        "Obrigado. Partilhe os detalhes nas suas proprias palavras e eu ajudarei a organizar o proximo passo seguro.",
    },
    appointments: {
      intro:
        "Posso indicar o fluxo de marcacao certo sem quebrar a experiencia dentro das Mensagens.",
      title: "Onde quer comecar?",
      subtitle:
        "Escolha o caminho certo e eu encaminho para o fluxo AfriMed adequado.",
    },
    healthQuestion:
      "Escreva a sua pergunta nas suas proprias palavras. Vou responder de forma pratica e segura. O suporte linguistico esta a expandir.",
    urgentHelp:
      "Se isto parecer uma emergencia, contacte os servicos locais de emergencia ou procure cuidados urgentes presenciais imediatamente.",
    patientSupport: {
      medicinesDisabled:
        "As explicacoes sobre medicamentos estao desativadas em Accessibility & Fola. Pode voltar a ativa-las em Settings quando quiser.",
      medicinesPrompt:
        "Partilhe o nome do medicamento, a dose ou o texto da receita e eu explicarei em linguagem simples.",
      questionsDisabled:
        "A preparacao de perguntas esta desativada em Accessibility & Fola. Ative-a em Settings se quiser ajuda da Fola nisto.",
      questionsPrompt:
        "Diga-me o objetivo da consulta ou a sua preocupacao e eu ajudarei a preparar perguntas uteis antes de ver o medico.",
      visitNotesDisabled:
        "Os resumos das notas da consulta estao desativados em Accessibility & Fola. Pode voltar a ativa-los em Settings.",
      visitNotesPrompt:
        "Cole aqui as notas da consulta, o relatorio ou a atualizacao e eu ajudarei a resumir os pontos principais.",
    },
    doctorLane:
      "Escreva o que precisa numa mensagem e eu ajudo a partir desta conversa. Os rascunhos SOAP aqui usam apenas a conversa desta thread.",
    doctorSoapSupportText:
      "Os rascunhos SOAP aqui usam apenas a conversa atual. A revisao do medico e obrigatoria antes da utilizacao.",
    doctorAdmin: {
      title: "Com o que precisa de ajuda no encaminhamento?",
      intro: "Escolha o proximo passo operacional e eu mantenho tudo dentro das Mensagens.",
      supportText:
        "A Fola pode ajudar a preparar seguimento, escalacao e mensagens ao paciente sem quebrar o contexto da conversa.",
      followUpTitle: "Verificacao de marcacao de seguimento",
      followUpBody:
        "Confirme se o paciente precisa de uma marcacao de seguimento, registe o prazo esperado e prepare uma resposta curta com o proximo passo seguro.",
      escalationTitle: "Revisao de escalacao",
      escalationBody:
        "Use isto quando a urgencia estiver a aumentar, os sinais de alarme nao estiverem claros ou o paciente puder precisar de avaliacao presencial. Resuma o risco com clareza antes de responder.",
    },
    doctorCards: {
      sourceLabel: "Origem",
      keyPointsTitle: "Pontos principais",
      openItemsTitle: "Pontos em aberto",
      noStrongSummary:
        "Ainda nao ha detalhe suficiente do paciente nesta conversa para produzir um resumo mais forte.",
      patientRaisedPrefix: "Paciente referiu",
      latestUpdatePrefix: "Atualizacao mais recente",
      clinicianResponsePrefix: "Resposta clinica ate agora",
      recentContextPrefix: "O contexto recente do paciente centra-se em",
      noPatientMessage: "Ainda nao foi captada nenhuma mensagem do paciente.",
      noLaterPatientUpdate: "Nao existe atualizacao posterior alem da mensagem inicial.",
      noClinicianReply: "Ainda nao existe resposta clinica registada.",
      confirmTimeline: "Confirme cronologia, gravidade e quaisquer sinais de alarme.",
      collectHistory: "Recolha a historia do paciente antes de fechar os proximos passos.",
      followUpBooking: "Verifique se e necessario seguimento, marcacao ou escalacao.",
      sections: {
        subjective: "Subjetivo",
        objective: "Objetivo",
        assessment: "Avaliacao",
        plan: "Plano",
      },
      limitedSymptoms: "Os sintomas relatados pelo paciente ainda sao limitados nesta conversa.",
      noObjectiveFindings:
        "Nao foram documentados achados objetivos, sinais vitais ou resultados de exame nesta conversa.",
      provisionalAssessment:
        "Avaliacao provisoria baseada apenas em mensagens. Precisa de revisao clinica com historia completa e sinais de alarme.",
      planPrefix: "Continue o seguimento clinico e esclareca",
      planFallback:
        "Esclareca gravidade, duracao e o proximo passo seguro. Considere marcacao ou escalacao se o risco aumentar.",
      confidenceNote:
        "Rascunho criado apenas a partir da conversa atual. A revisao do medico e obrigatoria antes de guardar ou partilhar clinicamente.",
      replySupportText: "Edite o tom e a precisao clinica antes de enviar.",
      useAsDraft: "Usar como rascunho",
      replyPrefix:
        "Obrigado pela atualizacao. Registei que",
      replySuffix:
        "Continue a vigiar os sintomas e diga-me se algo piorar, especialmente dificuldade respiratoria, dor intensa ou mudanca rapida.",
      replyFallback:
        "Obrigado pela atualizacao. Revisei a conversa e orientarei o proximo passo seguro assim que houver um pouco mais de detalhe.",
    },
    smartRouting: {
      urgentBridge:
        "Isto parece precisar de avaliacao humana urgente.",
      adminBridge:
        "Posso encaminhar ajuda com consultas a partir daqui sem quebrar a experiencia nas Mensagens.",
      doctorBridge:
        "Reconheci um pedido de fluxo medico. Aqui estao as acoes Fola nativas para essa trilha.",
    },
  },
  fr: {
    askAnything: "Posez n'importe quelle question a Fola",
    patientWelcome:
      "Je peux vous aider a verifier des symptomes, gerer l'administratif des rendez-vous et preparer des etapes sures. Le support linguistique s'etend.",
    patientStarterTitle: "Comment puis-je aider aujourd'hui ?",
    patientStarterIntro:
      "Choisissez un demarrage guide ou saisissez votre message ci-dessous.",
    doctorWelcome:
      "Je peux aider avec les resumes de conversation, les brouillons de reponse, les brouillons SOAP bases sur le fil et l'orientation administrative dans les Messages AfriMed.",
    doctorStarterTitle: "Choisissez une tache Fola",
    doctorStarterIntro:
      "Choisissez le flux voulu. Les brouillons SOAP ici utilisent uniquement le fil de messages actif.",
    actions: {
      checkSymptoms: "Verifier les symptomes",
      appointmentsHelp: "Aide rendez-vous",
      askHealthQuestion: "Poser une question de sante",
      urgentHelp: "Aide urgente",
      summarizeThread: "Resumer la conversation",
      draftSoap: "Brouillon SOAP du fil",
      draftReply: "Brouillon de reponse",
      adminRouting: "Aide d'orientation",
      followUpBooking: "Suivi de rendez-vous",
      escalationReview: "Revoir l'escalade",
      explainMedicines: "Expliquer mes medicaments",
      prepareQuestions: "M'aider a preparer des questions",
      summarizeVisitNotes: "Resumer mes notes de visite",
      selectHospital: "Choisir l'hopital",
      selectDoctor: "Choisir le medecin",
      yes: "Oui",
      no: "Non",
    },
    states: {
      thinking: "Je reflechis...",
      error: "Je ne peux pas repondre pour le moment. Veuillez reessayer.",
      imageReceived: "J'ai bien recu votre image.",
      imageReviewLimited:
        "Je peux aider a preparer les prochaines etapes, mais je ne peux pas encore analyser les images directement dans le chat.",
      doctorNeedsThread:
        "Ouvrez Fola depuis une conversation patient pour que je puisse construire cela a partir du fil reel.",
    },
    symptom: {
      intro:
        "Allons etape par etape pour garder l'aide sure et utile.",
      categoryTitle: "Quelle est la preoccupation principale ?",
      categoryIntro:
        "Choisissez l'option la plus proche pour commencer le tri.",
      categories: {
        rash: "Eruption ou changement cutane",
        fever: "Fievre",
        pain: "Douleur",
        breathing: "Toux ou respiration",
        stomach: "Trouble digestif",
        other: "Autres symptomes",
      },
      durationTitle: "Depuis quand cela dure-t-il ?",
      durationIntro:
        "Une duree approximative m'aide a poser la question la plus sure ensuite.",
      durations: {
        today: "Commence aujourd'hui",
        threeDays: "1 a 3 jours",
        moreThanThreeDays: "Plus de 3 jours",
      },
      redFlagTitle: "Y a-t-il des signes d'alerte urgents maintenant ?",
      redFlagIntro:
        "Choisissez Oui si le symptome est severe, s'aggrave vite ou touche la respiration, un gonflement, un malaise ou une confusion.",
      writeup:
        "Decrivez ou cela se passe, quand cela a commence et si c'est demangeant, douloureux, chaud, gonfle ou en train de s'etendre.",
      redFlagNoticeTitle: "Une evaluation humaine urgente est necessaire",
      redFlagNoticeBody:
        "Veuillez chercher des soins urgents en personne ou contacter les services d'urgence si le symptome est severe, s'etend vite ou affecte la respiration.",
      lowRiskFollowUp:
        "Merci. Partagez les details avec vos mots et je vous aiderai a organiser l'etape suivante la plus sure.",
    },
    appointments: {
      intro:
        "Je peux vous orienter vers le bon parcours de reservation sans casser l'experience Messages.",
      title: "Ou voulez-vous commencer ?",
      subtitle:
        "Choisissez le bon parcours et je vous dirige vers le bon flux AfriMed.",
    },
    healthQuestion:
      "Posez votre question avec vos propres mots. Je garderai la reponse pratique et sure. Le support linguistique s'etend.",
    urgentHelp:
      "Si cela ressemble a une urgence, contactez les services d'urgence locaux ou rendez-vous immediatement vers des soins urgents en personne.",
    patientSupport: {
      medicinesDisabled:
        "Les explications sur les medicaments sont actuellement desactivees dans Accessibility & Fola. Vous pouvez les reactiver dans Settings quand vous voulez.",
      medicinesPrompt:
        "Partagez le nom du medicament, la dose ou le texte de prescription et je l'expliquerai en langage simple.",
      questionsDisabled:
        "La preparation des questions est actuellement desactivee dans Accessibility & Fola. Activez-la dans Settings si vous voulez l'aide de Fola pour cela.",
      questionsPrompt:
        "Dites-moi l'objectif du rendez-vous ou votre preoccupation et je vous aiderai a preparer des questions utiles avant de voir votre medecin.",
      visitNotesDisabled:
        "Les resumes de notes de visite sont actuellement desactives dans Accessibility & Fola. Vous pouvez les reactiver dans Settings.",
      visitNotesPrompt:
        "Collez ici les notes de visite, le rapport ou la mise a jour de consultation et je vous aiderai a resumer les points cles.",
    },
    doctorLane:
      "Dites-moi ce dont vous avez besoin en un seul message et je vous aiderai depuis ce fil. Les brouillons SOAP ici utilisent uniquement le fil de messages actif.",
    doctorSoapSupportText:
      "Les brouillons SOAP ici utilisent uniquement le fil de messages actuel. La relecture du medecin est obligatoire avant utilisation.",
    doctorAdmin: {
      title: "De quoi avez-vous besoin pour l'orientation ?",
      intro: "Choisissez l'etape operationnelle suivante et je la garde dans Messages.",
      supportText:
        "Fola peut aider a preparer le suivi, l'escalade et le message au patient sans casser le contexte du fil.",
      followUpTitle: "Verification du rendez-vous de suivi",
      followUpBody:
        "Confirmez si le patient a besoin d'un rendez-vous de suivi, notez le delai attendu et preparez une courte reponse avec la prochaine etape sure.",
      escalationTitle: "Revue d'escalade",
      escalationBody:
        "Utilisez ceci quand l'urgence augmente, quand les signaux d'alerte sont incertains ou quand le patient peut avoir besoin d'une evaluation en personne. Resumez clairement le risque avant de repondre.",
    },
    doctorCards: {
      sourceLabel: "Source",
      keyPointsTitle: "Points cles",
      openItemsTitle: "Points ouverts",
      noStrongSummary:
        "Il n'y a pas encore assez de details patient dans ce fil pour produire un resume plus solide.",
      patientRaisedPrefix: "Le patient a signale",
      latestUpdatePrefix: "Derniere mise a jour",
      clinicianResponsePrefix: "Reponse clinique jusqu'ici",
      recentContextPrefix: "Le contexte recent du patient porte sur",
      noPatientMessage: "Aucun message patient n'a encore ete capture.",
      noLaterPatientUpdate:
        "Aucune mise a jour patient ulterieure au message initial.",
      noClinicianReply: "Aucune reponse clinique n'a encore ete capturee.",
      confirmTimeline:
        "Confirmez la chronologie, la gravite et tout signe d'alerte.",
      collectHistory:
        "Recueillez l'histoire du patient avant de finaliser la suite.",
      followUpBooking:
        "Verifiez si un suivi, une reservation ou une escalation est necessaire.",
      sections: {
        subjective: "Subjectif",
        objective: "Objectif",
        assessment: "Evaluation",
        plan: "Plan",
      },
      limitedSymptoms:
        "Les symptomes rapportes par le patient restent limites dans ce fil.",
      noObjectiveFindings:
        "Aucun constat objectif, signe vital ou resultat d'examen n'a ete documente dans ce fil.",
      provisionalAssessment:
        "Evaluation provisoire basee uniquement sur les messages. A revoir par un clinicien avec l'historique complet et les signes d'alerte.",
      planPrefix: "Poursuivre le suivi clinique et clarifier",
      planFallback:
        "Clarifier la gravite, la duree et la prochaine etape sure. Envisager reservation ou escalation si le risque augmente.",
      confidenceNote:
        "Brouillon cree uniquement a partir du fil de messages actuel. La relecture du medecin est obligatoire avant enregistrement ou partage clinique.",
      replySupportText: "Adaptez le ton et la precision clinique avant l'envoi.",
      useAsDraft: "Utiliser comme brouillon",
      replyPrefix:
        "Merci pour votre mise a jour. J'ai note que",
      replySuffix:
        "Continuez a surveiller vos symptomes et dites-moi si quelque chose s'aggrave, surtout une difficulte respiratoire, une douleur intense ou un changement rapide.",
      replyFallback:
        "Merci pour votre mise a jour. J'ai relu le fil et je guiderai la prochaine etape sure des qu'un peu plus de details sera disponible.",
    },
    smartRouting: {
      urgentBridge:
        "Cela semble necessiter une evaluation humaine urgente.",
      adminBridge:
        "Je peux orienter l'aide rendez-vous depuis ici sans casser l'experience Messages.",
      doctorBridge:
        "J'ai reconnu une demande de flux medecin. Voici les actions Fola natives pour cette voie.",
    },
  },
  de: {
    askAnything: "Frag Fola alles",
    patientWelcome:
      "Ich kann beim Prufen von Symptomen, bei Terminfragen und bei sicheren nachsten Schritten helfen. Sprachunterstutzung wird im Staging erweitert.",
    patientStarterTitle: "Wobei kann ich heute helfen?",
    patientStarterIntro:
      "Wahle einen gefuhrten Start oder schreibe unten deine Nachricht.",
    doctorWelcome:
      "Ich kann bei Verlaufszusammenfassungen, Antwortentwurfen, threadbasierten SOAP-Entwurfen und administrativem Routing in AfriMed Messages helfen.",
    doctorStarterTitle: "Wahle eine Fola-Aufgabe",
    doctorStarterIntro:
      "Wahle den gewunschten Ablauf. SOAP-Entwurfe hier nutzen nur den aktuellen Nachrichtenverlauf.",
    actions: {
      checkSymptoms: "Symptome prufen",
      appointmentsHelp: "Terminhilfe",
      askHealthQuestion: "Gesundheitsfrage stellen",
      urgentHelp: "Dringende Hilfe",
      summarizeThread: "Verlauf zusammenfassen",
      draftSoap: "SOAP aus Verlauf entwerfen",
      draftReply: "Antwort entwerfen",
      adminRouting: "Routing-Hilfe",
      followUpBooking: "Nachsorge-Termin",
      escalationReview: "Eskalation prufen",
      explainMedicines: "Meine Medikamente erklaren",
      prepareQuestions: "Mir beim Vorbereiten von Fragen helfen",
      summarizeVisitNotes: "Meine Besuchsnotizen zusammenfassen",
      selectHospital: "Klinik auswahlen",
      selectDoctor: "Arzt auswahlen",
      yes: "Ja",
      no: "Nein",
    },
    states: {
      thinking: "Ich denke nach...",
      error: "Ich konnte gerade nicht antworten. Bitte versuche es erneut.",
      imageReceived: "Ich habe dein Bild erhalten.",
      imageReviewLimited:
        "Ich kann bei den nachsten Schritten helfen, aber ich kann Bilder im Chat noch nicht direkt beurteilen.",
      doctorNeedsThread:
        "Offne Fola aus einem Patiententhread, damit ich dies aus der echten Unterhaltung erstellen kann.",
    },
    symptom: {
      intro:
        "Lass uns Schritt fur Schritt vorgehen, damit die Hilfe sicher und nutzlich bleibt.",
      categoryTitle: "Was ist das Hauptproblem?",
      categoryIntro:
        "Wahle die passende Option fur den Start der Symptomabfrage.",
      categories: {
        rash: "Ausschlag oder Hautveranderung",
        fever: "Fieber",
        pain: "Schmerzen",
        breathing: "Husten oder Atmung",
        stomach: "Magenbeschwerden",
        other: "Andere Symptome",
      },
      durationTitle: "Wie lange besteht das schon?",
      durationIntro:
        "Ein grober Zeitraum hilft mir bei der sichersten nachsten Frage.",
      durations: {
        today: "Heute begonnen",
        threeDays: "1 bis 3 Tage",
        moreThanThreeDays: "Mehr als 3 Tage",
      },
      redFlagTitle: "Gibt es gerade akute Warnzeichen?",
      redFlagIntro:
        "Wahle Ja, wenn das Symptom stark ist, schnell schlimmer wird oder Atmung, Schwellung, Ohnmacht oder Verwirrung betrifft.",
      writeup:
        "Schreibe bitte, wo es ist, wann es begonnen hat und ob es juckt, schmerzt, warm, geschwollen oder sich ausbreitend ist.",
      redFlagNoticeTitle: "Das braucht dringend menschliche Abklarung",
      redFlagNoticeBody:
        "Bitte suche jetzt dringend personliche Hilfe oder kontaktiere den Notdienst, wenn das Symptom stark ist, sich schnell ausbreitet oder die Atmung betrifft.",
      lowRiskFollowUp:
        "Danke. Teile die Details in deinen eigenen Worten und ich helfe dir beim sichersten nachsten Schritt.",
    },
    appointments: {
      intro:
        "Ich kann dich in den richtigen Buchungsfluss leiten, ohne dass sich Messages fremd anfuhlt.",
      title: "Wo mochtest du anfangen?",
      subtitle:
        "Wahle den passenden Weg und ich leite dich in den richtigen AfriMed-Fluss.",
    },
    healthQuestion:
      "Schreibe deine Frage in deinen eigenen Worten. Ich halte die Antwort praktisch und sicher. Sprachunterstutzung wird im Staging erweitert.",
    urgentHelp:
      "Wenn das wie ein Notfall wirkt, kontaktiere bitte sofort den lokalen Notdienst oder suche dringend personliche Versorgung auf.",
    patientSupport: {
      medicinesDisabled:
        "Medikamentenerklarungen sind in Accessibility & Fola derzeit ausgeschaltet. Du kannst sie in Settings jederzeit wieder aktivieren.",
      medicinesPrompt:
        "Teile den Medikamentennamen, die Dosis oder den Rezepttext mit und ich erklare es in alltaglicher Sprache.",
      questionsDisabled:
        "Die Fragenvorbereitung ist in Accessibility & Fola derzeit ausgeschaltet. Aktiviere sie in Settings, wenn Fola dabei helfen soll.",
      questionsPrompt:
        "Nenne mir das Ziel des Termins oder dein Anliegen, und ich helfe dir dabei, vor dem Arzttermin nutzliche Fragen vorzubereiten.",
      visitNotesDisabled:
        "Zusammenfassungen von Besuchsnotizen sind in Accessibility & Fola derzeit ausgeschaltet. Du kannst sie in Settings wieder aktivieren.",
      visitNotesPrompt:
        "Fuge hier die Besuchsnotizen, den Bericht oder das Beratungsupdate ein, und ich helfe beim Zusammenfassen der Kernpunkte.",
    },
    doctorLane:
      "Schreibe mir in einer Nachricht, was du brauchst, und ich helfe dir aus diesem Verlauf heraus. SOAP-Entwurfe hier nutzen nur den aktuellen Nachrichtenverlauf.",
    doctorSoapSupportText:
      "SOAP-Entwurfe hier nutzen nur den aktuellen Nachrichtenverlauf. Eine arztliche Prufung ist vor der Nutzung erforderlich.",
    doctorAdmin: {
      title: "Wobei brauchst du Routing-Hilfe?",
      intro: "Wahle den nachsten operativen Schritt und ich halte ihn in Messages.",
      supportText:
        "Fola kann Nachsorge, Eskalation und patientenseitige Antworten vorbereiten, ohne den Verlaufskontext zu verlieren.",
      followUpTitle: "Nachsorge-Termin prufen",
      followUpBody:
        "Prufe, ob der Patient einen Nachsorge-Termin braucht, notiere den erwarteten Zeitraum und bereite eine kurze Antwort mit dem nachsten sicheren Schritt vor.",
      escalationTitle: "Eskalation prufen",
      escalationBody:
        "Nutze dies, wenn die Dringlichkeit steigt, Warnzeichen unklar sind oder der Patient moglicherweise personlich untersucht werden sollte. Fasse das Risiko klar zusammen, bevor du antwortest.",
    },
    doctorCards: {
      sourceLabel: "Quelle",
      keyPointsTitle: "Kernpunkte",
      openItemsTitle: "Offene Punkte",
      noStrongSummary:
        "Es gibt in diesem Verlauf noch nicht genug Patientendetails fur eine starkere Zusammenfassung.",
      patientRaisedPrefix: "Patient meldete",
      latestUpdatePrefix: "Neueste Aktualisierung",
      clinicianResponsePrefix: "Bisherige klinische Antwort",
      recentContextPrefix: "Der aktuelle Patientenkontext dreht sich um",
      noPatientMessage: "Noch keine Patientennachricht erfasst.",
      noLaterPatientUpdate: "Keine spatere Patientenaktualisierung uber die erste Nachricht hinaus.",
      noClinicianReply: "Noch keine klinische Antwort erfasst.",
      confirmTimeline:
        "Zeitverlauf, Schweregrad und etwaige Warnzeichen bestatigen.",
      collectHistory:
        "Patientenvorgeschichte erfassen, bevor die nachsten Schritte festgelegt werden.",
      followUpBooking:
        "Prufen, ob Nachverfolgung, Buchung oder Eskalation notig ist.",
      sections: {
        subjective: "Subjektiv",
        objective: "Objektiv",
        assessment: "Beurteilung",
        plan: "Plan",
      },
      limitedSymptoms:
        "Die vom Patienten geschilderten Symptome sind in diesem Verlauf noch begrenzt.",
      noObjectiveFindings:
        "Im Nachrichtenverlauf wurden keine objektiven Befunde, Vitalwerte oder Untersuchungsergebnisse dokumentiert.",
      provisionalAssessment:
        "Vorlaufige Beurteilung nur auf Basis des Nachrichtenverlaufs. Klinische Prufung mit voller Anamnese und Warnzeichen erforderlich.",
      planPrefix: "Klinische Nachverfolgung fortsetzen und klaren",
      planFallback:
        "Schweregrad, Dauer und den nachsten sicheren Schritt klaren. Bei steigendem Risiko Buchung oder Eskalation erwagen.",
      confidenceNote:
        "Entwurf nur aus dem aktuellen Nachrichtenverlauf erstellt. Arztliche Prufung ist vor klinischem Speichern oder Teilen erforderlich.",
      replySupportText: "Vor dem Senden Ton und klinische Genauigkeit anpassen.",
      useAsDraft: "Als Entwurf verwenden",
      replyPrefix:
        "Danke fur das Update. Ich habe vermerkt, dass",
      replySuffix:
        "Bitte beobachte deine Symptome weiter und gib mir Bescheid, wenn sich etwas verschlechtert, besonders bei Atemnot, starken Schmerzen oder rascher Veranderung.",
      replyFallback:
        "Danke fur das Update. Ich habe den Verlauf gepruft und leite den nachsten sicheren Schritt an, sobald etwas mehr Detail vorliegt.",
    },
    smartRouting: {
      urgentBridge:
        "Das klingt nach einem Fall fur dringende menschliche Abklarung.",
      adminBridge:
        "Ich kann Terminanliegen direkt von hier aus weiterleiten, ohne die Messages-Erfahrung zu brechen.",
      doctorBridge:
        "Ich habe eine arztbezogene Workflow-Anfrage erkannt. Hier sind die nativen Fola-Aktionen fur diese Spur.",
    },
  },
};

function createItemId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function resolveCopy(locale) {
  const normalized = normalizeLanguageCode(locale);
  return COPY[normalized] || COPY[normalized.split("-")[0]] || COPY["en-GB"];
}

function resolvePrefs(rawPrefs) {
  const prefs = rawPrefs && typeof rawPrefs === "object" ? rawPrefs : {};
  return {
    folaSummaries:
      typeof prefs.folaSummaries === "boolean" ? prefs.folaSummaries : true,
    folaExplainMeds:
      typeof prefs.folaExplainMeds === "boolean" ? prefs.folaExplainMeds : true,
    folaPrepQuestions:
      typeof prefs.folaPrepQuestions === "boolean" ? prefs.folaPrepQuestions : true,
  };
}

function normalizeOutputType(value, fallback = "support") {
  return value === "support" || value === "admin" || value === "draft"
    ? value
    : fallback;
}

function normalizeSourceContext(rawSourceContext) {
  const input =
    rawSourceContext && typeof rawSourceContext === "object"
      ? rawSourceContext
      : {};

  return {
    kind:
      input.kind === "appointment_context" ||
      input.kind === "prescription_context" ||
      input.kind === "message_thread"
        ? input.kind
        : "message_thread",
    conversationId: String(input.conversationId || "").trim() || null,
    appointmentId: String(input.appointmentId || "").trim() || null,
    prescriptionId: String(input.prescriptionId || "").trim() || null,
  };
}

function createText(text, extra = {}) {
  return {
    id: createItemId("fola-text"),
    type: "text",
    side: "left",
    text,
    time: "Now",
    outputType: normalizeOutputType(extra.outputType, "support"),
    sourceContext: normalizeSourceContext(extra.sourceContext),
    ...extra,
  };
}

function createPrompt(promptKey, config) {
  return {
    id: createItemId(promptKey),
    type: "fola-prompt",
    side: "left",
    time: "Now",
    promptKey,
    title: config.title,
    intro: config.intro || "",
    layout: config.layout || "stack",
    choices: Array.isArray(config.choices) ? config.choices : [],
    supportText: config.supportText || "",
    outputType: normalizeOutputType(config.outputType, "support"),
    sourceContext: normalizeSourceContext(config.sourceContext),
  };
}

function createNotice(noticeKey, config) {
  return {
    id: createItemId(noticeKey),
    type: "fola-notice",
    side: "left",
    time: "Now",
    noticeKey,
    title: config.title,
    body: config.body,
    tone: config.tone || "info",
    cta: config.cta || null,
    outputType: normalizeOutputType(config.outputType, "support"),
    sourceContext: normalizeSourceContext(config.sourceContext),
  };
}

function createPatientStarter(copy, rawPrefs = null, sourceContext = null) {
  const prefs = resolvePrefs(rawPrefs);
  const choices = [
    {
      id: "patient-symptom-check",
      label: copy.actions.checkSymptoms,
      variant: "default",
    },
    {
      id: "patient-appointments-help",
      label: copy.actions.appointmentsHelp,
      variant: "tonal",
    },
    {
      id: "patient-health-question",
      label: copy.actions.askHealthQuestion,
      variant: "ghost",
    },
    {
      id: "patient-urgent-help",
      label: copy.actions.urgentHelp,
      variant: "ghost",
    },
  ];

  if (prefs.folaExplainMeds) {
    choices.push({
      id: "patient-explain-medicines",
      label: copy.actions.explainMedicines,
      variant: "ghost",
    });
  }

  if (prefs.folaPrepQuestions) {
    choices.push({
      id: "patient-prepare-questions",
      label: copy.actions.prepareQuestions,
      variant: "ghost",
    });
  }

  if (prefs.folaSummaries) {
    choices.push({
      id: "patient-summarize-visit-notes",
      label: copy.actions.summarizeVisitNotes,
      variant: "ghost",
    });
  }

  return createPrompt("patient-starter", {
    title: copy.patientStarterTitle,
    intro: copy.patientStarterIntro,
    layout: "grid",
    choices,
    outputType: "support",
    sourceContext,
  });
}

function hasThreadSource(sourceContext = null) {
  return String(sourceContext?.conversationId || "").trim().length > 0;
}

function createDoctorStarter(copy, sourceContext = null) {
  const hasSourceThread = hasThreadSource(sourceContext);
  const choices = hasSourceThread
    ? [
        {
          id: "doctor-summarize-thread",
          label: copy.actions.summarizeThread,
          variant: "tonal",
        },
        {
          id: "doctor-draft-soap",
          label: copy.actions.draftSoap,
          variant: "tonal",
        },
        {
          id: "doctor-draft-reply",
          label: copy.actions.draftReply,
          variant: "ghost",
        },
        {
          id: "doctor-admin-routing",
          label: copy.actions.adminRouting,
          variant: "ghost",
        },
      ]
    : [
        {
          id: "doctor-admin-routing",
          label: copy.actions.adminRouting,
          variant: "tonal",
        },
      ];

  return createPrompt("doctor-starter", {
    title: copy.doctorStarterTitle,
    intro: hasSourceThread
      ? copy.doctorStarterIntro
      : copy.states.doctorNeedsThread,
    layout: "grid",
    choices,
    supportText: hasSourceThread
      ? copy.doctorSoapSupportText || ""
      : copy.states.doctorNeedsThread || "",
    outputType: "support",
    sourceContext,
  });
}

function createSymptomCategoryPrompt(copy, sourceContext = null) {
  return createPrompt("patient-symptom-category", {
    title: copy.symptom.categoryTitle,
    intro: `${copy.symptom.intro} ${copy.symptom.categoryIntro}`,
    choices: [
      {
        id: "symptom-rash",
        label: copy.symptom.categories.rash,
        variant: "ghost",
      },
      {
        id: "symptom-fever",
        label: copy.symptom.categories.fever,
        variant: "ghost",
      },
      {
        id: "symptom-pain",
        label: copy.symptom.categories.pain,
        variant: "ghost",
      },
      {
        id: "symptom-breathing",
        label: copy.symptom.categories.breathing,
        variant: "ghost",
      },
      {
        id: "symptom-stomach",
        label: copy.symptom.categories.stomach,
        variant: "ghost",
      },
      {
        id: "symptom-other",
        label: copy.symptom.categories.other,
        variant: "ghost",
      },
    ],
    outputType: "support",
    sourceContext,
  });
}

function createDurationPrompt(copy, sourceContext = null) {
  return createPrompt("patient-symptom-duration", {
    title: copy.symptom.durationTitle,
    intro: copy.symptom.durationIntro,
    choices: [
      {
        id: "duration-today",
        label: copy.symptom.durations.today,
        variant: "ghost",
      },
      {
        id: "duration-three-days",
        label: copy.symptom.durations.threeDays,
        variant: "ghost",
      },
      {
        id: "duration-more-than-three-days",
        label: copy.symptom.durations.moreThanThreeDays,
        variant: "ghost",
      },
    ],
    outputType: "support",
    sourceContext,
  });
}

function createRedFlagPrompt(copy, sourceContext = null) {
  return createPrompt("patient-symptom-red-flags", {
    title: copy.symptom.redFlagTitle,
    intro: copy.symptom.redFlagIntro,
    layout: "grid",
    choices: [
      {
        id: "symptom-red-flags-yes",
        label: copy.actions.yes,
        variant: "default",
      },
      {
        id: "symptom-red-flags-no",
        label: copy.actions.no,
        variant: "tonal",
      },
    ],
    outputType: "support",
    sourceContext,
  });
}

function createAppointmentsPrompt(copy, sourceContext = null) {
  return createPrompt("patient-appointments-routing", {
    title: copy.appointments.title,
    intro: `${copy.appointments.intro} ${copy.appointments.subtitle}`,
    layout: "grid",
    choices: [
      {
        id: "appointments-select-hospital",
        label: copy.actions.selectHospital,
        variant: "ghost",
      },
      {
        id: "appointments-select-doctor",
        label: copy.actions.selectDoctor,
        variant: "default",
      },
    ],
    outputType: "admin",
    sourceContext,
  });
}

function createDoctorRoutingPrompt(copy, sourceContext = null) {
  const hasSourceThread = hasThreadSource(sourceContext);
  const choices = hasSourceThread
    ? [
        {
          id: "doctor-summarize-thread",
          label: copy.actions.summarizeThread,
          variant: "tonal",
        },
        {
          id: "doctor-draft-soap",
          label: copy.actions.draftSoap,
          variant: "tonal",
        },
        {
          id: "doctor-draft-reply",
          label: copy.actions.draftReply,
          variant: "ghost",
        },
        {
          id: "doctor-admin-routing",
          label: copy.actions.adminRouting,
          variant: "ghost",
        },
      ]
    : [
        {
          id: "doctor-admin-routing",
          label: copy.actions.adminRouting,
          variant: "tonal",
        },
      ];

  return createPrompt("doctor-starter", {
    title: copy.doctorStarterTitle,
    intro: hasSourceThread
      ? copy.smartRouting.doctorBridge
      : copy.states.doctorNeedsThread,
    layout: "grid",
    choices,
    supportText: hasSourceThread
      ? copy.doctorSoapSupportText || ""
      : copy.states.doctorNeedsThread || "",
    outputType: "support",
    sourceContext,
  });
}

function createDoctorAdminPrompt(copy, sourceContext = null) {
  return createPrompt("doctor-admin-routing", {
    title: copy.doctorAdmin?.title || copy.actions.adminRouting,
    intro: copy.doctorAdmin?.intro || copy.doctorLane,
    layout: "grid",
    choices: [
      {
        id: "doctor-admin-follow-up",
        label: copy.actions.followUpBooking || copy.actions.adminRouting,
        variant: "tonal",
      },
      {
        id: "doctor-admin-escalation",
        label: copy.actions.escalationReview || copy.actions.adminRouting,
        variant: "ghost",
      },
      {
        id: "doctor-draft-reply",
        label: copy.actions.draftReply,
        variant: "ghost",
      },
      {
        id: "doctor-summarize-thread",
        label: copy.actions.summarizeThread,
        variant: "ghost",
      },
    ],
    supportText: copy.doctorAdmin?.supportText || "",
    outputType: "admin",
    sourceContext,
  });
}

const PHARMACY_ACTIONS = {
  roles: "Explain pharmacy roles",
  staffInvite: "Prepare staff invite copy",
  csv: "CSV import guidance",
  handoff: "Fulfilment handoff steps",
  inventory: "Stock and inventory setup",
  payments: "Payment and settlement concepts",
  boundaries: "What Fola cannot do",
};

const PHARMACY_WELCOME =
  "Hello, my name is Fola.\n\nI can help with pharmacy operations, prescriptions, inventory, payments, branch setup, staff roles, and pharmacy workflows.\n\nYou can ask me a question below or select one of the prompts provided.\n\nI do not diagnose patients, override clinicians, change prescriptions clinically, or recommend unsafe substitutions.\n\nHow can I help you today?";

function createPharmacyStarter(copy, sourceContext = null) {
  const actions = copy.pharmacyActions || PHARMACY_ACTIONS;

  return createPrompt("pharmacy-starter", {
    title: "Choose a pharmacy support task",
    intro:
      "Use Fola for pharmacy operations guidance only. Keep patient-specific clinical decisions and prescription routing outside this chat.",
    layout: "grid",
    choices: [
      { id: "pharmacy-roles", label: actions.roles, variant: "default" },
      { id: "pharmacy-staff-invite", label: actions.staffInvite, variant: "tonal" },
      { id: "pharmacy-csv", label: actions.csv, variant: "ghost" },
      { id: "pharmacy-handoff", label: actions.handoff, variant: "ghost" },
      { id: "pharmacy-inventory", label: actions.inventory, variant: "ghost" },
      { id: "pharmacy-payments", label: actions.payments, variant: "ghost" },
      { id: "pharmacy-boundaries", label: actions.boundaries, variant: "ghost" },
    ],
    supportText:
      "Fola explains pharmacy workflows only. It does not change ownership, inventory, fulfilment, pricing, payments, or patient records.",
    outputType: "support",
    sourceContext,
  });
}

function buildPharmacyRolesReply() {
  return [
    "Pharmacy roles and permissions:",
    "",
    "Owner / superintendent pharmacist: manages the branch profile, verification details, staff invitations, role assignments, branch settings, fulfilment activity, pricing or quote review, and payout setup. They should retain protected ownership permissions and should not use staff roles for owner-only changes.",
    "",
    "Dispensing pharmacist: reviews incoming prescriptions, confirms stock, prepares or dispenses medicine, and updates fulfilment status. They cannot change ownership, remove the pharmacy, manage payment settings, or override the prescribing clinician.",
    "",
    "Inventory manager: maintains stock counts, reorder thresholds, medicine lines, suppliers, batch details, expiry dates, bin locations, and stock confidence. They cannot manage ownership, payout settings, staff access, or clinical prescription decisions.",
    "",
    "Finance lead: reviews medicine revenue, AfriMed™ fees, delivery collections, payment links, settlement previews, and recent transactions. They cannot change ownership, invite staff, complete clinical dispensing handoff, or promise live settlement where payout readiness is incomplete.",
    "",
    "Delivery lead: coordinates pickup or delivery handoff once the item is prepared and payment status allows release. They cannot change prescriptions clinically, set branch ownership, manage payout settings, or mark unavailable stock as fulfilled.",
    "",
    "Branch admin: supports routine branch operations, staff coordination, and non-clinical workflow updates. They cannot remove the owner, alter protected payout settings, expose AfriMed™ administration data, or override clinical prescription decisions.",
  ].join("\n");
}

function buildPharmacyInviteReply() {
  return [
    "Suggested branch staff invitation copy:",
    "",
    "Hello [Name],",
    "",
    "You are being invited to join [Branch name] on AfriMed™. Your invited email is [email address], and your assigned role is [role]. This role controls the branch tools you can access once you sign in.",
    "",
    "Your staff record will remain pending until you sign in with the invited email and branch access is linked. The branch owner retains protected permissions for ownership, payout settings, staff access, and other restricted actions.",
    "",
    "Please use the invitation link to access the branch workspace and review the tasks assigned to your role.",
    "",
    "Kind regards,",
    "[Owner / branch admin name]",
  ].join("\n");
}

function buildPharmacyCsvReply() {
  return [
    "CSV import guidance:",
    "",
    "Prepare one row per branch staff member. Include full name, email, phone if available, branch name, and role.",
    "",
    "Role values should align to the pharmacy roles AfriMed™ understands: dispensing pharmacist, inventory manager, finance lead, delivery lead, or branch admin.",
    "",
    "After import, review pending invites before treating anyone as active. Staff should remain pending until they sign in with the invited email and the branch link is confirmed.",
  ].join("\n");
}

function buildPharmacyHandoffReply() {
  return [
    "Fulfilment handoff steps:",
    "",
    "1. Receive the prescription request in the branch queue.",
    "2. Open the order and review the prescribed item and patient-facing fulfilment details.",
    "3. Confirm stock honestly, or mark the item unavailable if the branch cannot fulfil it.",
    "4. Set the pharmacy price or prepare the quote where the workflow asks for it.",
    "5. Prepare the item for pickup or delivery.",
    "6. Wait for payment confirmation where payment is required before release.",
    "7. Release the item for pickup or delivery.",
    "8. Update the fulfilment status so the patient, clinician, and branch team can see progress.",
    "",
    "Do not override clinician prescriptions or make clinical substitutions through Fola. Use pharmacist judgement and the approved pharmacy workflow where substitution review is legally and clinically appropriate.",
  ].join("\n");
}

function buildPharmacyInventoryReply() {
  return [
    "Stock and inventory setup:",
    "",
    "For each medicine line, capture the item name, strength or detail, category, stock count, reorder threshold, unit price, supplier, branch, batch number, expiry date, bin location, and optional image.",
    "",
    "Keep stock availability honest. Do not mark items available if the branch cannot fulfil them.",
    "",
    "Make stock changes in the inventory screen so the branch record remains auditable.",
  ].join("\n");
}

function buildPharmacyAddStockItemReply() {
  return [
    "Add stock item guidance",
    "",
    "Item:",
    "Enter the medicine or product name.",
    "",
    "Stock ID:",
    "Use an internal stock identifier if your pharmacy uses one.",
    "",
    "Strength / detail:",
    "Record strength, formulation, pack size, or useful product detail.",
    "",
    "Category:",
    "Choose the category that best describes the item.",
    "",
    "Current stock:",
    "Enter the quantity currently available.",
    "",
    "Minimal stock:",
    "Enter the level at which reorder attention is needed.",
    "",
    "Unit price:",
    "Enter the price your pharmacy is prepared to charge.",
    "",
    "Supplier:",
    "Record the supplier or distributor if known.",
    "",
    "Branch:",
    "Select the branch holding the stock.",
    "",
    "Batch number:",
    "Use batch references where available.",
    "",
    "Expiry date:",
    "Record expiry information to support stock safety.",
    "",
    "Bin location:",
    "Record the shelf, cabinet, fridge, or storage location.",
    "",
    "Image URL:",
    "Optional product image reference.",
    "",
    "Keep availability and pricing honest.",
    "",
    "Only enter stock and prices your pharmacy can stand behind.",
  ].join("\n");
}

function buildPharmacyDashboardReply() {
  return [
    "Pharmacy dashboard overview",
    "",
    "• Received prescriptions shows prescription activity requiring review or fulfilment.",
    "",
    "• Consultations reflects appointment or consultation activity that may generate pharmacy work.",
    "",
    "• Sold items shows completed medicine activity.",
    "",
    "• Income shows medicine revenue visibility.",
    "",
    "• Most sold items helps identify frequently dispensed medicines.",
    "",
    "• Low-stock alerts highlight medicines that may require reorder attention.",
    "",
    "Use these cards to monitor branch activity and prioritise daily work.",
  ].join("\n");
}

function buildPharmacyPrescriptionOrderReply() {
  return [
    "Prescription order guidance",
    "",
    "Review the patient details, medication name, strength, quoted price, fulfilment mode, and current order status.",
    "",
    "Use the order actions to continue the workflow. If the medication, dose, interaction risk, substitution, or fulfilment details are unclear, contact the issuing clinician before proceeding.",
    "",
    "Fola can explain the workflow, but cannot change prescriptions clinically, approve substitutions, or override the prescribing clinician.",
  ].join("\n");
}

function buildPharmacyHelpReply() {
  return [
    "Pharmacy Help Centre guidance:",
    "",
    "Use the Help Centre to find pharmacy guidance on onboarding, profile verification, inventory, prescription fulfilment, payments, messages, alerts, team roles, and technical support.",
    "",
    "Use the Help Centre search field when you know the topic. Use Messages & Fola when you want operational explanation. Use Technical support when access, routing, hydration, or pharmacy data looks wrong.",
  ].join("\n");
}

function buildPharmacyPaymentsReply() {
  return [
    "Pharmacy payments overview",
    "",
    "• Medicine revenue represents the value of medicines supplied.",
    "",
    "• AfriMed™ fees should be tracked separately from medicine revenue.",
    "",
    "• Delivery collections should be recorded independently.",
    "",
    "• Tax handling should follow local market requirements.",
    "",
    "• Payment readiness determines whether payment links can be used.",
    "",
    "• Settlement previews help estimate expected pharmacy proceeds.",
    "",
    "• Recent transactions provide visibility into completed activity.",
    "",
    "Only authorised pharmacy personnel should manage payout and settlement settings.",
  ].join("\n");
}

function buildPharmacyBoundariesReply() {
  return [
    "Pharmacy Fola boundaries:",
    "",
    "Fola can help explain roles, staff invites, CSV prep, inventory setup, fulfilment handoff, and general payment concepts.",
    "",
    "Fola cannot diagnose, override clinicians, change prescriptions clinically, recommend unsafe substitutions, expose AfriMed™ administration data, invent stock, invent pricing, or invent regulatory approval.",
  ].join("\n");
}

function buildPharmacyOperationsReply(text = "") {
  const normalized = String(text || "").trim().toLowerCase();

  if (includesAny(normalized, ["dashboard card", "dashboard cards", "prescription counts", "income", "sold items", "dashboard overview"])) {
    return buildPharmacyDashboardReply();
  }
  if (includesAny(normalized, ["prescription order", "this order", "order status", "quoted price", "issuing clinician"])) {
    return buildPharmacyPrescriptionOrderReply();
  }
  if (includesAny(normalized, ["help centre", "help center", "technical support", "find pharmacy guidance"])) {
    return buildPharmacyHelpReply();
  }
  if (includesAny(normalized, ["add stock item form", "add stock item", "complete the add stock", "item field", "stock id", "image url"])) {
    return buildPharmacyAddStockItemReply();
  }
  if (includesAny(normalized, ["role", "roles", "owner", "pharmacist", "assistant", "staff", "permission", "privilege"])) {
    return buildPharmacyRolesReply();
  }
  if (includesAny(normalized, ["csv", "spreadsheet", "import", "upload staff"])) {
    return buildPharmacyCsvReply();
  }
  if (includesAny(normalized, ["invite", "invitation", "staff copy", "member copy", "add staff"])) {
    return buildPharmacyInviteReply();
  }
  if (includesAny(normalized, ["payment", "settlement", "payout", "finance", "reconciliation", "price", "pricing", "quote"])) {
    return buildPharmacyPaymentsReply();
  }
  if (includesAny(normalized, ["handoff", "delivery", "dispatch", "fulfilment", "fulfillment", "dispense", "pickup"])) {
    return buildPharmacyHandoffReply();
  }
  if (includesAny(normalized, ["inventory", "stock", "sku", "low-stock", "low stock", "availability", "cold-chain", "cold chain"])) {
    return buildPharmacyInventoryReply();
  }
  if (includesAny(normalized, ["cannot", "can't", "not allowed", "boundary", "boundaries", "clinical", "diagnose", "route", "admin", "kpi"])) {
    return buildPharmacyBoundariesReply();
  }

  return [
    "I can help with this as pharmacy operations guidance.",
    "",
    buildPharmacyRolesReply(),
    "",
    "Tell me whether you want role assignment, invite copy, CSV import, inventory setup, fulfilment handoff, or payment concept guidance.",
  ].join("\n");
}

export function getFolaUiCopy(locale) {
  return resolveCopy(locale);
}

export function getInitialFolaItems({
  role = "patient",
  locale = "en-GB",
  preferences = null,
  sourceContext = null,
}) {
  const copy = resolveCopy(locale);

  if (role === "pharmacy") {
    return [
      createText(copy.pharmacyWelcome || PHARMACY_WELCOME, {
        outputType: "support",
        sourceContext,
      }),
      createPharmacyStarter(copy, sourceContext),
    ];
  }

  if (role === "doctor") {
    const hasSourceThread = hasThreadSource(sourceContext);
    return [
      createText(hasSourceThread ? copy.doctorWelcome : copy.states.doctorNeedsThread, {
        outputType: "support",
        sourceContext,
      }),
      createDoctorStarter(copy, sourceContext),
    ];
  }

  return [
    createText(copy.patientWelcome, { outputType: "support", sourceContext }),
    createPatientStarter(copy, preferences, sourceContext),
  ];
}

export function getActiveFolaPrompt(items) {
  const allItems = Array.isArray(items) ? items : [];

  for (let index = allItems.length - 1; index >= 0; index -= 1) {
    const item = allItems[index];
    if (item?.type !== "fola-prompt") continue;

    const answered = allItems
      .slice(index + 1)
      .some((nextItem) => nextItem?.side === "right" && nextItem?.type !== "date");

    if (!answered) return item;
  }

  return null;
}

export function resolveFolaSelection({
  choiceId,
  role = "patient",
  locale = "en-GB",
  preferences = null,
  sourceContext = null,
}) {
  const copy = resolveCopy(locale);
  const prefs = resolvePrefs(preferences);

  switch (choiceId) {
    case "pharmacy-roles":
      return {
        userText: PHARMACY_ACTIONS.roles,
        followUps: [
          createText(buildPharmacyRolesReply(), { outputType: "support", sourceContext }),
        ],
      };

    case "pharmacy-staff-invite":
      return {
        userText: PHARMACY_ACTIONS.staffInvite,
        followUps: [
          createText(buildPharmacyInviteReply(), { outputType: "draft", sourceContext }),
        ],
      };

    case "pharmacy-csv":
      return {
        userText: PHARMACY_ACTIONS.csv,
        followUps: [
          createText(buildPharmacyCsvReply(), { outputType: "support", sourceContext }),
        ],
      };

    case "pharmacy-handoff":
      return {
        userText: PHARMACY_ACTIONS.handoff,
        followUps: [
          createText(buildPharmacyHandoffReply(), { outputType: "support", sourceContext }),
        ],
      };

    case "pharmacy-inventory":
      return {
        userText: PHARMACY_ACTIONS.inventory,
        followUps: [
          createText(buildPharmacyInventoryReply(), { outputType: "support", sourceContext }),
        ],
      };

    case "pharmacy-payments":
      return {
        userText: PHARMACY_ACTIONS.payments,
        followUps: [
          createText(buildPharmacyPaymentsReply(), { outputType: "support", sourceContext }),
        ],
      };

    case "pharmacy-boundaries":
      return {
        userText: PHARMACY_ACTIONS.boundaries,
        followUps: [
          createText(buildPharmacyBoundariesReply(), { outputType: "support", sourceContext }),
        ],
      };

    case "patient-symptom-check":
      return {
        userText: copy.actions.checkSymptoms,
        followUps: [
          createText(copy.symptom.intro, { outputType: "support", sourceContext }),
          createSymptomCategoryPrompt(copy, sourceContext),
        ],
      };

    case "patient-appointments-help":
      return {
        userText: copy.actions.appointmentsHelp,
        followUps: [createAppointmentsPrompt(copy, sourceContext)],
      };

    case "patient-health-question":
      return {
        userText: copy.actions.askHealthQuestion,
        followUps: [createText(copy.healthQuestion, { outputType: "support", sourceContext })],
      };

    case "patient-urgent-help":
      return {
        userText: copy.actions.urgentHelp,
        followUps: [
          createNotice("patient-urgent-help", {
            title: copy.symptom.redFlagNoticeTitle,
            body: copy.urgentHelp,
            tone: "critical",
            outputType: "support",
            sourceContext,
            cta: {
              id: "appointments-select-hospital",
              label: copy.actions.selectHospital,
            },
          }),
        ],
      };

    case "patient-explain-medicines":
      if (!prefs.folaExplainMeds) {
        return {
          userText: copy.actions.explainMedicines,
          followUps: [
            createText(
              copy.patientSupport?.medicinesDisabled ||
                "Medicine explanations are currently turned off in Accessibility & Fola. You can turn them back on in Settings whenever you want.",
              { outputType: "support", sourceContext }
            ),
          ],
        };
      }
      return {
        userText: copy.actions.explainMedicines,
        followUps: [
          createText(
            cleanContextText(sourceContext?.myEhr?.medicationSummary)
              ? buildMedicineExplanationReply("", sourceContext)
              : copy.patientSupport?.medicinesPrompt ||
                "Share the medicine name, dose, or prescription text and I will explain it in everyday language.",
            { outputType: "support", sourceContext }
          ),
        ],
      };

    case "patient-prepare-questions":
      if (!prefs.folaPrepQuestions) {
        return {
          userText: copy.actions.prepareQuestions,
          followUps: [
            createText(
              copy.patientSupport?.questionsDisabled ||
                "Question preparation is currently turned off in Accessibility & Fola. Turn it on in Settings if you want Fola to help with this.",
              { outputType: "support", sourceContext }
            ),
          ],
        };
      }
      return {
        userText: copy.actions.prepareQuestions,
        followUps: [
          createText(
            hasPatientVisibleVisitContext(sourceContext)
              ? buildPrepareQuestionsReply(sourceContext)
              : copy.patientSupport?.questionsPrompt ||
                "Tell me the appointment goal or concern, and I will help you prepare useful questions before you see your doctor.",
            { outputType: "support", sourceContext }
          ),
        ],
      };

    case "patient-summarize-visit-notes":
      if (!prefs.folaSummaries) {
        return {
          userText: copy.actions.summarizeVisitNotes,
          followUps: [
            createText(
              copy.patientSupport?.visitNotesDisabled ||
                "Visit-note summaries are currently turned off in Accessibility & Fola. You can enable them again in Settings.",
              { outputType: "support", sourceContext }
            ),
          ],
        };
      }
      return {
        userText: copy.actions.summarizeVisitNotes,
        followUps: [
          createText(
            hasPatientVisibleVisitContext(sourceContext)
              ? buildVisitNotesSummaryReply(sourceContext)
              : copy.patientSupport?.visitNotesPrompt ||
                "Paste the visit notes, report, or consultation update here and I will help summarise the key points.",
            { outputType: "draft", sourceContext }
          ),
        ],
      };

    case "symptom-rash":
    case "symptom-fever":
    case "symptom-pain":
    case "symptom-breathing":
    case "symptom-stomach":
    case "symptom-other": {
      const labelMap = {
        "symptom-rash": copy.symptom.categories.rash,
        "symptom-fever": copy.symptom.categories.fever,
        "symptom-pain": copy.symptom.categories.pain,
        "symptom-breathing": copy.symptom.categories.breathing,
        "symptom-stomach": copy.symptom.categories.stomach,
        "symptom-other": copy.symptom.categories.other,
      };

      return {
        userText: labelMap[choiceId],
        followUps: [createDurationPrompt(copy, sourceContext)],
      };
    }

    case "duration-today":
    case "duration-three-days":
    case "duration-more-than-three-days": {
      const labelMap = {
        "duration-today": copy.symptom.durations.today,
        "duration-three-days": copy.symptom.durations.threeDays,
        "duration-more-than-three-days": copy.symptom.durations.moreThanThreeDays,
      };

      return {
        userText: labelMap[choiceId],
        followUps: [createRedFlagPrompt(copy, sourceContext)],
      };
    }

    case "symptom-red-flags-yes":
      return {
        userText: copy.actions.yes,
        followUps: [
          createNotice("patient-symptom-red-flags", {
            title: copy.symptom.redFlagNoticeTitle,
            body: copy.symptom.redFlagNoticeBody,
            tone: "critical",
            outputType: "support",
            sourceContext,
            cta: {
              id: "appointments-select-hospital",
              label: copy.actions.selectHospital,
            },
          }),
        ],
      };

    case "symptom-red-flags-no":
      return {
        userText: copy.actions.no,
        followUps: [
          createText(copy.symptom.lowRiskFollowUp, { outputType: "support", sourceContext }),
          createText(copy.symptom.writeup, { outputType: "support", sourceContext }),
        ],
      };

    case "appointments-select-hospital":
      return {
        userText: copy.actions.selectHospital,
        route: "/appointments/hospitals",
      };

    case "appointments-select-doctor":
      return {
        userText: copy.actions.selectDoctor,
        route: "/appointments/doctors",
      };

    case "doctor-summarize-thread":
    case "doctor-draft-soap":
    case "doctor-draft-reply":
      return {
        userText:
          role === "doctor"
            ? createDoctorStarter(copy).choices.find((choice) => choice.id === choiceId)?.label ||
              ""
            : "",
        followUps: [createText(copy.doctorLane, { outputType: "support", sourceContext })],
      };

    case "doctor-admin-routing":
      return {
        userText: copy.actions.adminRouting,
        followUps: [createDoctorAdminPrompt(copy, sourceContext)],
      };

    case "doctor-admin-follow-up":
      return {
        userText: copy.actions.followUpBooking || copy.actions.adminRouting,
        followUps: [
          createNotice("doctor-admin-follow-up", {
            title:
              copy.doctorAdmin?.followUpTitle ||
              copy.actions.followUpBooking ||
              copy.actions.adminRouting,
            body: copy.doctorAdmin?.followUpBody || copy.doctorLane,
            outputType: "admin",
            sourceContext,
            cta: {
              id: "doctor-draft-reply",
              label: copy.actions.draftReply,
            },
          }),
        ],
      };

    case "doctor-admin-escalation":
      return {
        userText: copy.actions.escalationReview || copy.actions.adminRouting,
        followUps: [
          createNotice("doctor-admin-escalation", {
            title:
              copy.doctorAdmin?.escalationTitle ||
              copy.actions.escalationReview ||
              copy.actions.adminRouting,
            body: copy.doctorAdmin?.escalationBody || copy.doctorLane,
            tone: "critical",
            outputType: "admin",
            sourceContext,
            cta: {
              id: "doctor-summarize-thread",
              label: copy.actions.summarizeThread,
            },
          }),
        ],
      };

    default:
      return null;
  }
}

const URGENT_KEYWORDS = [
  "chest pain",
  "trouble breathing",
  "cannot breathe",
  "can't breathe",
  "difficulty breathing",
  "faint",
  "fainted",
  "passing out",
  "seizure",
  "severe bleeding",
  "bleeding heavily",
  "swollen tongue",
  "swollen throat",
  "anaphylaxis",
  "suicidal",
  "suicide",
  "overdose",
];

const APPOINTMENT_KEYWORDS = [
  "appointment",
  "book",
  "booking",
  "reschedule",
  "cancel",
  "schedule",
  "doctor",
  "hospital",
  "clinic",
];

const MY_EHR_CONTEXT_KEYWORDS = [
  "what does this mean",
  "my health record",
  "myehr",
  "my ehr",
  "visit note",
  "visit notes",
  "vitalrep",
  "vital rep",
  "clinical note",
  "doctor note",
  "what did the doctor say",
  "what was recorded",
  "explain my record",
  "vitals",
  "vital signs",
  "blood pressure",
  "heart rate",
  "temperature",
  "oxygen saturation",
];

const DOCTOR_WORKFLOW_KEYWORDS = [
  "summary",
  "summarise",
  "summarize",
  "soap",
  "draft reply",
  "reply draft",
  "route this",
  "admin help",
];

function includesAny(text, keywords) {
  return keywords.some((keyword) => text.includes(keyword));
}

function cleanContextText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function describeVitalsSource(source) {
  const normalized = cleanContextText(source).toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === "patient_reported") return "patient-reported";
  if (normalized === "clinician_captured") return "clinician-captured";
  if (normalized === "previous_record") return "referenced from a previous record";
  return "not captured";
}

function buildMyEhrContextReply(copy, sourceContext = null) {
  const context = sourceContext?.myEhr || null;
  if (!context) {
    return (
      copy.patientSupport?.noRecordContext ||
      "I do not have a saved myEHR visit note in this chat yet. Open My Health Record to review visit cards, prescriptions, tests, and notes."
    );
  }

  const details = [
    cleanContextText(context.reasonForVisit) ? `reason: ${cleanContextText(context.reasonForVisit)}` : "",
    cleanContextText(context.assessment) ? `assessment: ${cleanContextText(context.assessment)}` : "",
    cleanContextText(context.treatmentNextSteps) ? `next steps: ${cleanContextText(context.treatmentNextSteps)}` : "",
    cleanContextText(context.medicationSummary) ? `medicine/prescription: ${cleanContextText(context.medicationSummary)}` : "",
    cleanContextText(context.testsLabsRequested) ? `tests/labs: ${cleanContextText(context.testsLabsRequested)}` : "",
    `vitals source: ${describeVitalsSource(context.vitalsSource)}`,
    cleanContextText(context.followUpAdvice) ? `follow-up: ${cleanContextText(context.followUpAdvice)}` : "",
  ].filter(Boolean);

  if (details.length === 0) {
    return (
      copy.patientSupport?.noRecordContext ||
      "I do not have a saved myEHR visit note in this chat yet. Open My Health Record to review visit cards, prescriptions, tests, and notes."
    );
  }

  const prefix =
    copy.patientSupport?.recordContextPrefix ||
    "This myEHR note says your clinician recorded";
  const safety =
    copy.patientSupport?.recordContextSafety ||
    "If symptoms worsen, or if anything is unclear, please contact your clinician.";

  return `${prefix} ${details.join("; ")}. These vitals were recorded as ${describeVitalsSource(
    context.vitalsSource
  )}, based on the visit record. ${safety}`;
}

function buildMedicineExplanationReply(text, sourceContext = null) {
  const original = cleanContextText(text);
  const savedMedicationText = cleanContextText(sourceContext?.myEhr?.medicationSummary);
  const savedReasonText = cleanContextText(sourceContext?.myEhr?.reasonForVisit);
  const inputForClues = savedMedicationText || original;
  const decoded = [
    [/\b(od|once daily|daily)\b/i, "once daily"],
    [/\b(bd|bid|twice daily)\b/i, "twice daily"],
    [/\b(tds|tid|three times daily)\b/i, "three times daily"],
    [/\b(qid|four times daily)\b/i, "four times daily"],
    [/\b(prn|when needed|as needed)\b/i, "only when needed or as prescribed"],
    [/\b(po|by mouth|orally)\b/i, "by mouth"],
    [/\b(nocte|at night|nightly)\b/i, "at night"],
    [/\b(mane|morning)\b/i, "in the morning"],
    [/\b(after meals|after food|with food)\b/i, "with or after food"],
    [/\b(before meals|before food)\b/i, "before food"],
  ]
    .filter(([pattern]) => pattern.test(inputForClues))
    .map(([, label]) => label);

  const strength = inputForClues.match(/\b\d+(?:\.\d+)?\s?(?:mg|mcg|g|ml|iu|units?)\b/i)?.[0] || "";
  const medicineText = inputForClues.replace(/\s+/g, " ").slice(0, 180);
  const decodedText = decoded.length
    ? `I can read these instruction clues from what you shared: ${Array.from(new Set(decoded)).join(", ")}.`
    : "I do not see a clear timing instruction in the available text.";
  const strengthText = strength
    ? `The strength or amount shown appears to be ${strength}.`
    : "I do not see a clear strength such as mg or ml in the available text.";
  const sourceText = medicineText
    ? `${savedMedicationText ? "Your saved myEHR medication/prescription note says" : "You shared"}: "${medicineText}".`
    : "I do not have a medicine name or prescription line yet.";
  const reasonText = savedReasonText
    ? `The linked visit context says the recorded reason was: "${savedReasonText.slice(0, 140)}".`
    : "";

  return `${sourceText} ${reasonText} ${strengthText} ${decodedText} Please use this only to understand the wording. Fola cannot confirm why this medicine was prescribed, diagnose, change the dose, stop treatment, or replace your clinician or pharmacist. If the label is unclear, the dose looks different from what you were told, you feel worse, or you notice a possible reaction, contact your clinician or pharmacist before changing anything.`;
}

function buildPrepareQuestionsReply(sourceContext = null) {
  const ehr = sourceContext?.myEhr || null;
  const reason = cleanContextText(ehr?.reasonForVisit);
  const assessment = cleanContextText(ehr?.assessment);
  const treatment = cleanContextText(ehr?.treatmentNextSteps);
  const medicines = cleanContextText(ehr?.medicationSummary);
  const tests = cleanContextText(ehr?.testsLabsRequested);
  const followUp = cleanContextText(ehr?.followUpAdvice);

  if (!ehr || ![reason, assessment, treatment, medicines, tests, followUp].some(Boolean)) {
    return "I do not have saved appointment, prescription, or visit-note context in this chat yet. You can still ask: What should I watch for? When should I come back? What should I do if symptoms worsen? Are there tests, medicines, or follow-up steps I should understand? Fola cannot diagnose or change treatment, but can help you prepare clearer questions for your clinician.";
  }

  const prompts = [];
  if (reason) prompts.push(`For the recorded reason "${reason.slice(0, 120)}", what changes should I monitor?`);
  if (assessment) prompts.push(`Can you explain the recorded assessment in everyday language?`);
  if (treatment) prompts.push(`Which next steps are most important, and when should I follow up?`);
  if (medicines) prompts.push(`How should I take the listed medicine safely, and what should I do if the label is unclear?`);
  if (tests) prompts.push(`What are these tests/labs checking, and how will I receive the results?`);
  if (followUp) prompts.push(`When should I seek help sooner than the planned follow-up?`);

  return `Based on the patient-visible AfriMed™ context in this chat, useful questions could be: ${prompts
    .slice(0, 5)
    .map((item, index) => `${index + 1}. ${item}`)
    .join(" ")} Fola is helping you prepare questions only; it is not diagnosing, changing treatment, or replacing your clinician.`;
}

function hasPatientVisibleVisitContext(sourceContext = null) {
  const ehr = sourceContext?.myEhr || null;
  if (!ehr) return false;
  return [
    ehr.reasonForVisit,
    ehr.assessment,
    ehr.treatmentNextSteps,
    ehr.medicationSummary,
    ehr.testsLabsRequested,
    ehr.followUpAdvice,
  ].some((value) => cleanContextText(value));
}

function buildVisitNotesSummaryReply(sourceContext = null) {
  const ehr = sourceContext?.myEhr || null;
  if (!hasPatientVisibleVisitContext(sourceContext)) {
    return "I do not have patient-visible visit notes in this chat yet. Open myEHR or share the patient-visible note, and I can help explain the wording without making a diagnosis or changing treatment.";
  }

  const sourceLabel = cleanContextText(ehr?.vitalRepStatus)
    ? "VitalRep/myEHR context"
    : "patient-visible AfriMed™ context";
  const visit = [cleanContextText(ehr?.visitDate), cleanContextText(ehr?.service)]
    .filter(Boolean)
    .join(" · ");
  const items = [
    cleanContextText(ehr?.reasonForVisit)
      ? `appointment note - reason recorded: ${cleanContextText(ehr.reasonForVisit)}`
      : "",
    cleanContextText(ehr?.assessment)
      ? `clinician note - recorded assessment wording: ${cleanContextText(ehr.assessment)}`
      : "",
    cleanContextText(ehr?.treatmentNextSteps)
      ? `clinician note - next steps: ${cleanContextText(ehr.treatmentNextSteps)}`
      : "",
    cleanContextText(ehr?.medicationSummary)
      ? `prescription context - medicine note: ${cleanContextText(ehr.medicationSummary)}`
      : "",
    cleanContextText(ehr?.testsLabsRequested)
      ? `appointment note - tests/labs: ${cleanContextText(ehr.testsLabsRequested)}`
      : "",
    cleanContextText(ehr?.followUpAdvice)
      ? `clinician note - follow-up advice: ${cleanContextText(ehr.followUpAdvice)}`
      : "",
  ].filter(Boolean);

  return `Source: ${sourceLabel}${visit ? ` (${visit})` : ""}. Summary: ${items
    .slice(0, 6)
    .join("; ")}. This explains patient-visible wording only. Fola is not making a diagnosis, changing treatment, or exposing private clinician/internal notes. If anything is unclear or worrying, ask your clinician for clarification.`;
}

export function resolveFolaFreeformInput({
  text,
  role = "patient",
  locale = "en-GB",
  preferences = null,
  sourceContext = null,
}) {
  const copy = resolveCopy(locale);
  const prefs = resolvePrefs(preferences);
  const normalized = String(text || "").trim().toLowerCase();
  if (!normalized) return null;

  if (role === "pharmacy") {
    return {
      skipRemote: true,
      followUps: [
        createText(buildPharmacyOperationsReply(text), {
          outputType: "support",
          sourceContext,
        }),
      ],
    };
  }

  const medicineKeywords = [
    "medicine",
    "medicines",
    "medication",
    "medications",
    "prescription",
    "dose",
    "tablet",
    "capsule",
  ];
  const prepQuestionKeywords = [
    "prepare questions",
    "questions to ask",
    "what should i ask",
    "what must i ask",
    "appointment questions",
  ];
  const summaryKeywords = [
    "summarise my visit notes",
    "summarize my visit notes",
    "summarise visit notes",
    "summarize visit notes",
    "summarise my notes",
    "summarize my notes",
    "summarise this report",
    "summarize this report",
  ];

  if (role !== "doctor" && includesAny(normalized, MY_EHR_CONTEXT_KEYWORDS)) {
    return {
      skipRemote: true,
      followUps: [
        createText(buildMyEhrContextReply(copy, sourceContext), {
          outputType: "support",
          sourceContext,
        }),
      ],
    };
  }

  if (!prefs.folaExplainMeds && includesAny(normalized, medicineKeywords)) {
    return {
      skipRemote: true,
      followUps: [
        createText(
          copy.patientSupport?.medicinesDisabled ||
            "Medicine explanations are currently turned off in Accessibility & Fola. You can turn them back on in Settings whenever you want.",
          { outputType: "support", sourceContext }
        ),
      ],
    };
  }

  if (role !== "doctor" && prefs.folaExplainMeds && includesAny(normalized, medicineKeywords)) {
    return {
      skipRemote: true,
      followUps: [
        createText(buildMedicineExplanationReply(text, sourceContext), {
          outputType: "support",
          sourceContext,
        }),
      ],
    };
  }

  if (!prefs.folaPrepQuestions && includesAny(normalized, prepQuestionKeywords)) {
    return {
      skipRemote: true,
      followUps: [
        createText(
          copy.patientSupport?.questionsDisabled ||
            "Question preparation is currently turned off in Accessibility & Fola. Turn it on in Settings if you want Fola to help with this.",
          { outputType: "support", sourceContext }
        ),
      ],
    };
  }

  if (role !== "doctor" && prefs.folaPrepQuestions && includesAny(normalized, prepQuestionKeywords)) {
    return {
      skipRemote: true,
      followUps: [
        createText(buildPrepareQuestionsReply(sourceContext), {
          outputType: "support",
          sourceContext,
        }),
      ],
    };
  }

  if (!prefs.folaSummaries && includesAny(normalized, summaryKeywords)) {
    return {
      skipRemote: true,
      followUps: [
        createText(
          copy.patientSupport?.visitNotesDisabled ||
            "Visit-note summaries are currently turned off in Accessibility & Fola. You can enable them again in Settings.",
          { outputType: "support", sourceContext }
        ),
      ],
    };
  }

  if (role !== "doctor" && prefs.folaSummaries && includesAny(normalized, summaryKeywords)) {
    return {
      skipRemote: true,
      followUps: [
        createText(buildVisitNotesSummaryReply(sourceContext), {
          outputType: "support",
          sourceContext,
        }),
      ],
    };
  }

  if (includesAny(normalized, URGENT_KEYWORDS)) {
    return {
      skipRemote: true,
      followUps: [
        createText(copy.smartRouting.urgentBridge, { outputType: "support", sourceContext }),
        createNotice("patient-symptom-red-flags", {
          title: copy.symptom.redFlagNoticeTitle,
          body: copy.symptom.redFlagNoticeBody,
          tone: "critical",
          outputType: "support",
          sourceContext,
          cta: {
            id: "appointments-select-hospital",
            label: copy.actions.selectHospital,
          },
        }),
      ],
    };
  }

  if (role === "doctor" && includesAny(normalized, DOCTOR_WORKFLOW_KEYWORDS)) {
    return {
      skipRemote: true,
      followUps: [createDoctorRoutingPrompt(copy, sourceContext)],
    };
  }

  if (includesAny(normalized, APPOINTMENT_KEYWORDS)) {
    return {
      skipRemote: true,
      followUps: [
        createText(copy.smartRouting.adminBridge, { outputType: "admin", sourceContext }),
        createAppointmentsPrompt(copy, sourceContext),
      ],
    };
  }

  return null;
}
